import React, { useState, useContext, useMemo } from 'react';
import { Box, Stepper, Step, StepLabel, Paper, Grid, Typography, CircularProgress, Button } from '@mui/material';
import { SettingsContext } from '../context/SettingsContext';
import QuestionStep from './QuestionStep';
import BusinessContext from './BusinessContext';
import ProblemStatementInput from './ProblemStatementInput';
import EmailResults from './EmailResults';
import Logo from './Logo';
import AnalyzerErrorBoundary from './ErrorBoundary';
import { BACKGROUND_COLORS, STEPS } from '../constants/analyzerConstants';
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useAnalyzer } from '../hooks/useAnalyzer';
import { useTheme } from '@mui/material/styles';
import PageLayout from './PageLayout';
import IntroContent from './IntroContent';
import Footer from './Footer';

const WhyAnalyzer = () => {
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(-2);
    const [businessContext, setBusinessContext] = useState(null);
    const [problemStatement, setProblemStatement] = useState('');
    const { settings, setSettings } = useContext(SettingsContext);
    
    const {
        answers,
        rawAnswers,
        loadingStates,
        handleAnswer
    } = useAnalyzer(businessContext, problemStatement, settings);

    const handleBackStep = () => {
        if (activeStep === -0.5) {
            setActiveStep(-1);
            setProblemStatement(''); // Clear problem statement when going back to business context

        } else if (activeStep === 0) {
            setActiveStep(-0.5);
        } else {
            setActiveStep(activeStep - 1);
        }
    };

    const handleContextSubmit = (context) => {
        setBusinessContext(context);
        setActiveStep(-0.5);
    };

    const handleProblemStatementSubmit = (statement) => {
        setProblemStatement(statement);
        setActiveStep(0);
    };

    const handleAnswerSubmit = async (answer) => {
        const newStep = await handleAnswer(answer);
        setActiveStep(activeStep + 1);
    };

    const renderAnswers = useMemo(() => {
        if (!Array.isArray(rawAnswers)) return null;
        
        return (
            <Box sx={{ mb: 4, mt: 4 }}>
                <Grid container spacing={2}>
                    <Grid item container spacing={2} sx={{ mb: 3 }}>
                        {rawAnswers.slice(0, Math.min(2, rawAnswers.length)).map((answer, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                                <Paper sx={{ 
                                    p: 2, 
                                    height: '100%',
                                    bgcolor: theme.palette.whyGradients[index]
                                }}>
                                    <Typography variant="subtitle1" color="white">
                                        Why {index + 1}:
                                    </Typography>
                                    <Typography color="white">{answer}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                    
                    {rawAnswers.length > 2 && (
                        <Grid item container spacing={2} sx={{ mb: 3 }}>
                            {rawAnswers.slice(2, 5).map((answer, index) => (
                                <Grid item xs={12} sm={4} key={index + 2}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        height: '100%',
                                        bgcolor: theme.palette.whyGradients[index + 2]
                                    }}>
                                        <Typography variant="subtitle1" color="white">
                                            Why {index + 3}:
                                        </Typography>
                                        <Typography color="white">{answer}</Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    }, [rawAnswers, theme]);

    const renderContent = () => {
        if (activeStep === -2) {
            return (
                <>
                    <IntroContent />
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Button 
                            variant="contained"
                            size="large"
                            onClick={() => setActiveStep(-1)}
                            sx={{ 
                                padding: '16px 32px',
                                fontSize: '1.2rem',
                                background: theme.palette.primary.main
                            }}
                        >
                            Get Started
                        </Button>
                    </Box>
                </>
            );
        }

        if (activeStep === -1) {
            return (
                <Box>
                    <BusinessContext onSubmit={handleContextSubmit} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={handleBackStep}
                            sx={{ mr: 2 }}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>
            );
        }

        if (activeStep === -0.5) {
            return (
                <Box>
                    <ProblemStatementInput 
                        businessContext={businessContext}
                        onSubmit={handleProblemStatementSubmit}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={handleBackStep}
                            sx={{ mr: 2 }}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>
            );
        }

        return (
            <>
                <Stepper activeStep={activeStep}>
                    {STEPS.map((step) => (
                        <Step key={step}>
                            <StepLabel>Why {step}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Paper sx={{ p: 2, mb: 2, mt: 3, backgroundColor: 'rgba(255,255,255,0.1)' }}>
                    <Typography variant="h6" color="white">
                        {businessContext.businessName}
                    </Typography>
                    <Typography variant="subtitle1" color="white">
                        Product: {businessContext.solution}
                    </Typography>
                    <Typography variant="subtitle1" color="white" sx={{ mt: 1 }}>
                        Problem: {problemStatement}
                    </Typography>
                </Paper>

                {renderAnswers}
        
                {activeStep === 5 && !answers[0]?.aiSuggestion ? (
                    <Box>
                        <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Ready to generate your analysis and playbook!
                            </Typography>
                            <Button 
                                variant="contained"
                                size="large"
                                onClick={() => handleAnswer(null)}
                                disabled={loadingStates.analysis}
                                sx={{ mt: 2 }}
                            >
                                Get Analysis and Playbook
                            </Button>
                            {loadingStates.analysis && (
                                <Box sx={{ mt: 3 }}>
                                    <CircularProgress />
                                    <Typography sx={{ mt: 2 }}>
                                        Getting Feedback from Gemini...
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={handleBackStep}
                                sx={{ mr: 2 }}
                            >
                                Back
                            </Button>
                        </Box>
                    </Box>
                ) : activeStep < 5 ? (
                    <Box>
                        <QuestionStep 
                            step={activeStep + 1}
                            onSubmit={handleAnswerSubmit}
                            loading={loadingStates.submission}
                            businessContext={businessContext}
                            problemStatement={problemStatement}
                            rawAnswers={rawAnswers} 
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={handleBackStep}
                                sx={{ mr: 2 }}
                            >
                                Back
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box>
                    <Paper 
                        sx={{ 
                            p: 4, 
                            mt: 4, 
                            color:'#010101',
                            backgroundColor: '#fefefe',
                            borderRadius: 1,
                            boxShadow: 3
                        }}
                    >
                        <Typography 
                            component="div"
                            sx={{
                                '& span': { display: 'block', mb: 2 },
                                lineHeight: 1.1,
                                fontSize: '1.1rem'
                            }}
                            dangerouslySetInnerHTML={{ 
                                __html: answers[0]?.aiSuggestion?.analysis
                            }} 
                        />
                    </Paper>
                    <EmailResults 
                        analysis={answers[0]?.aiSuggestion?.analysis}
                        businessContext={businessContext}
                        problemStatement={problemStatement}
                        rawAnswers={rawAnswers}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.location.reload()}
                        sx={{ mt: 3 }}
                    >
                        Start Over
                    </Button>
                </Box>
                )}
            </>
        );
    };

    return (
        <AnalyzerErrorBoundary>
            <Box sx={{ 
                maxWidth: 800, 
                margin: '0 auto', 
                padding: 3,
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column' 
            }}>
                {activeStep > -2 && (
                    <Typography variant="h1" 
                        sx={{ 
                            fontSize: { xs: '2.5rem', md: '4rem' },
                            fontWeight: 700,
                            mb: 3,
                            textAlign: 'center',
                            width: '100%',
                            background: 'linear-gradient(45deg, #2C5282 30%, #4299E1 90%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                        The WhyLine™: 5 Whys Strategy Framework
                    </Typography>
                )}
                {renderContent()}
                <Footer />
            </Box>
        </AnalyzerErrorBoundary>
    );
};

export default WhyAnalyzer;
